// Library page listing achievement tracks with the user's progress

import React, { useCallback, useMemo, useState } from 'react';
import { CircleAlertIcon, TrophyIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import SEO from '../../../seo/SEO';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CollectionToolbar,
  FilterControls,
  SortControl,
} from '../../../components/collectionToolbar';
import { useAchievementTracks, useRouteQueryState } from '../../../hooks';
import { SORT_DIRECTIONS, type SortDirection } from '../../../utilities/sort';
import AchievementTrackCard from './AchievementTrackCard';
import {
  ACHIEVEMENT_LEVELS,
  ACHIEVEMENT_SORT_FIELDS,
  ACHIEVEMENT_SORT_OPTIONS,
  ACHIEVEMENT_STATUS_LABELS,
  ACHIEVEMENT_STATUSES,
  type AchievementSortField,
  type AchievementStatusFilter,
  DEFAULT_ACHIEVEMENT_SORT_DIRECTION,
  DEFAULT_ACHIEVEMENT_SORT_FIELD,
  parseLevel,
  sortAchievementTracks,
  trackStatus,
} from './achievementSort';
import { isOneTime } from './achievementTrack';

const ACHIEVEMENTS_PATHNAME = '/library/achievements';

// Same grid as the game library, so the cards share its size.
const GRID_CLASS =
  'grid items-start gap-4 min-[1125px]:grid-cols-2 2xl:grid-cols-3';

const AchievementsLoadingGrid: React.FC = () => (
  <div className={GRID_CLASS}>
    {[0, 1, 2].map((index) => (
      <Card key={index} className="gap-0 overflow-hidden p-0">
        <Skeleton className="aspect-[16/10] w-full rounded-none" />
        <div className="flex h-16 items-center px-4 md:h-14">
          <Skeleton className="h-5 w-1/2 rounded-md" />
        </div>
      </Card>
    ))}
  </div>
);

const AchievementsPage: React.FC = () => {
  const { data: tracks, isError, refetch } = useAchievementTracks();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openHistoryKey, setOpenHistoryKey] = useState<string | null>(null);

  const { selected, values, updateRoute, updateEnums } = useRouteQueryState<
    { levels: number[] },
    {
      // Single-select: picking the other status replaces the current one.
      status: AchievementStatusFilter | null;
      sortField: AchievementSortField;
      sortDirection: SortDirection;
    }
  >({
    pathname: ACHIEVEMENTS_PATHNAME,
    searchParams,
    navigate,
    facets: {
      levels: {
        key: 'level',
        parse: parseLevel,
        compare: (left, right) => left - right,
      },
    },
    enums: {
      status: {
        key: 'status',
        values: ACHIEVEMENT_STATUSES,
        optional: true,
      },
      sortField: {
        key: 'sort',
        values: ACHIEVEMENT_SORT_FIELDS,
        default: DEFAULT_ACHIEVEMENT_SORT_FIELD,
      },
      sortDirection: {
        key: 'dir',
        values: SORT_DIRECTIONS,
        default: DEFAULT_ACHIEVEMENT_SORT_DIRECTION,
      },
    },
  });

  const selectedLevels = selected.levels;
  const { status, sortField, sortDirection } = values;

  const visibleTracks = useMemo(() => {
    const filtered = (tracks ?? []).filter(
      (track) =>
        (status === null || trackStatus(track) === status) &&
        // One-time tracks have no levels, so a level filter leaves them out.
        (selectedLevels.length === 0 ||
          (!isOneTime(track) && selectedLevels.includes(track.level)))
    );
    return sortAchievementTracks(filtered, sortField, sortDirection);
  }, [tracks, status, selectedLevels, sortField, sortDirection]);

  const setLevels = useCallback(
    (next: number[]) => {
      updateRoute({ levels: next });
    },
    [updateRoute]
  );

  const toggleLevel = useCallback(
    (value: string) => {
      const level = Number(value);
      setLevels(
        selectedLevels.includes(level)
          ? selectedLevels.filter((entry) => entry !== level)
          : [...selectedLevels, level]
      );
    },
    [selectedLevels, setLevels]
  );

  const toggleStatus = useCallback(
    (value: string) => {
      const next = value as AchievementStatusFilter;
      updateEnums({ status: status === next ? null : next });
    },
    [status, updateEnums]
  );

  const clearFilters = useCallback(() => {
    updateRoute({ levels: [] }, { enums: { status: null } });
  }, [updateRoute]);

  const activeFilterItems = useMemo(
    () => [
      ...(status
        ? [
            {
              id: `status:${status}`,
              label: ACHIEVEMENT_STATUS_LABELS[status],
              onRemove: () => updateEnums({ status: null }),
            },
          ]
        : []),
      ...selectedLevels.map((level) => ({
        id: `level:${level}`,
        label: `Level ${level}`,
        onRemove: () =>
          setLevels(selectedLevels.filter((entry) => entry !== level)),
      })),
    ],
    [status, selectedLevels, setLevels, updateEnums]
  );

  return (
    <div className="flex min-h-full w-full flex-col bg-background text-foreground">
      <SEO />

      <main className="mx-auto flex w-full max-w-screen-2xl flex-1 flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        {isError ? (
          <Empty className="border bg-card">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CircleAlertIcon />
              </EmptyMedia>
              <EmptyTitle>Oops! Something went wrong.</EmptyTitle>
              <EmptyDescription>
                We could not load your achievements.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button type="button" onClick={() => void refetch()}>
                Try again
              </Button>
            </EmptyContent>
          </Empty>
        ) : !tracks ? (
          <AchievementsLoadingGrid />
        ) : (
          <div className="flex flex-col gap-4">
            <CollectionToolbar
              filters={
                <FilterControls
                  groups={[
                    {
                      id: 'status',
                      label: 'Status',
                      options: ACHIEVEMENT_STATUSES.map((status) => ({
                        value: status,
                        label: ACHIEVEMENT_STATUS_LABELS[status],
                      })),
                      selectedValues: status ? [status] : [],
                      onToggle: toggleStatus,
                      searchable: false,
                    },
                    {
                      id: 'level',
                      label: 'Level',
                      options: ACHIEVEMENT_LEVELS.map((level) => ({
                        value: String(level),
                        label: `Level ${level}`,
                      })),
                      selectedValues: selectedLevels.map(String),
                      onToggle: toggleLevel,
                      searchable: false,
                    },
                  ]}
                  activeItems={activeFilterItems}
                  onClearAll={clearFilters}
                />
              }
              sort={
                <SortControl
                  options={ACHIEVEMENT_SORT_OPTIONS}
                  field={sortField}
                  direction={sortDirection}
                  onFieldChange={(next) => updateEnums({ sortField: next })}
                  onDirectionChange={(next) =>
                    updateEnums({ sortDirection: next })
                  }
                />
              }
            />
            {visibleTracks.length > 0 ? (
              <div className={GRID_CLASS}>
                {visibleTracks.map((track) => (
                  <AchievementTrackCard
                    key={track.achievementKey}
                    track={track}
                    historyOpen={openHistoryKey === track.achievementKey}
                    onToggleHistory={() =>
                      setOpenHistoryKey((prev) =>
                        prev === track.achievementKey
                          ? null
                          : track.achievementKey
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <Empty className="border bg-muted/10 py-16">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <TrophyIcon aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>No achievements match this filter</EmptyTitle>
                </EmptyHeader>
                <EmptyContent>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear all filters
                  </Button>
                </EmptyContent>
              </Empty>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AchievementsPage;
